import { useRouter } from "next/router";
import { useState } from "react";
import { useSearchContext } from "../components/Cards/SearchBar/SearchProvider";
import { formatYmd } from "../utils/dateBasics";
import trackSearch from "../utils/trackSearch";

type SearchProps = Partial<
  Pick<
    ReturnType<typeof useSearchContext>,
    "tags" | "locationId" | "from" | "until" | "numGuests" | "timeRanges"
  >
> & {
  ref?: string;
};

export default function useSearch() {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchContext = useSearchContext();

  const search = async ({
    from,
    locationId,
    numGuests,
    ref,
    tags,
    timeRanges,
    until,
  }: SearchProps) => {
    setLoading(true);
    trackSearch(router.asPath, ref);
    try {
      if (from) searchContext.setFrom(from);
      if (locationId) searchContext.setLocationId(locationId);
      if (numGuests) searchContext.setNumGuests(numGuests);
      if (tags) searchContext.setTags(tags);
      if (timeRanges) searchContext.setTimeRanges(timeRanges);
      if (until) searchContext.setUntil(until);

      await router.push({
        pathname: "/search",
        query: removeUndefined({
          from: formatYmd(from ?? searchContext.from),
          until: formatYmd(until ?? searchContext.until ?? searchContext.from),
          groupSize: numGuests ?? searchContext.numGuests,
          tagIds: JSON.stringify(Array.from(tags ?? searchContext.tags)),
          locationId: locationId ?? searchContext.locationId,
          timeRanges: timeRanges ?? JSON.stringify(searchContext.timeRanges),
          ref,
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  return { search, loading };
}

const removeUndefined = <T extends object>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
